import { useGridApiRef } from '@mui/x-data-grid-premium';
import { RiAddLine } from '@remixicon/react';
import { useCallback, useMemo } from 'react';
import type { WarehouseLocation } from 'src/api/warehouses.api';
import { PageBody } from 'src/components/containers/PageBody';
import { AgButton, AgText } from 'src/components/ui';
import { DeferredRetry, DeferredSkeleton } from 'src/modules/entities/deferred';
import { DataGrid, GridToolbarV2, useGridRows } from 'src/modules/grids';
import EditLocationModal from '../EditLocationModal';
import LocationGroupingsModal from '../LocationGroupingsModal';
import useWarehouseLocationsColumns from '../useWarehouseLocationsColumns';
import WarehouseModal from '../WarehouseModal';
import { WAREHOUSE_SECTION_NAMES, type WarehouseSectionsView } from './useWarehouseSections';
import { useWarehousePage } from './WarehousePageProviders';

const STORAGE_KEY = 'warehouse-locations';

const rowSearchKeys: (keyof WarehouseLocation)[] = ['slug', 'description'];

/** The page content, with the route loader's reader mounted once beside whichever state shows. */
export function WarehouseContent() {
  const { sections } = useWarehousePage();

  return (
    <>
      {sections.reader}
      <WarehouseContentBody />
    </>
  );
}

/** Retry for the first failed read, a skeleton until both reads answer, then the locations grid. */
function WarehouseContentBody() {
  const { sections } = useWarehousePage();
  const failed = WAREHOUSE_SECTION_NAMES.find(
    (name) => !sections.states[name].ready && sections.states[name].error != null
  );

  if (failed) {
    return (
      <PageBody>
        <DeferredRetry
          message={`Unable to load ${failed}.`}
          actionLabel={`Retry ${failed}`}
          onRetry={() => sections.retry(failed)}
        />
      </PageBody>
    );
  }
  if (WAREHOUSE_SECTION_NAMES.some((name) => !sections.states[name].ready)) {
    return (
      <PageBody>
        <DeferredSkeleton />
      </PageBody>
    );
  }
  if (!sections.warehouse) {
    return (
      <PageBody>
        <AgText size="14">Warehouse not found.</AgText>
      </PageBody>
    );
  }
  return <LoadedWarehouseContent warehouse={sections.warehouse} />;
}

type LoadedWarehouse = NonNullable<WarehouseSectionsView['warehouse']>;

/** Tag keys are a Postgres `text[]`, so no element is null; the generated `[String]!` type allows it. */
const isTagKey = (key: string | null): key is string => key !== null;

function LoadedWarehouseContent({ warehouse }: { warehouse: LoadedWarehouse }) {
  const {
    sections,
    editModalOpen,
    setEditModalOpen,
    groupingsModalOpen,
    setGroupingsModalOpen,
    locationEditor,
    setLocationEditor,
  } = useWarehousePage();
  const { locations, refetchWarehouse } = sections;
  const tagKeys = useMemo(() => warehouse.tagKeys.filter(isTagKey), [warehouse.tagKeys]);

  const apiRef = useGridApiRef();
  const enhancedGridState = useGridRows({ mode: 'local', storageKey: STORAGE_KEY, searchKeys: rowSearchKeys });

  const openLocationEditor = useCallback(
    (location: WarehouseLocation) => setLocationEditor({ location }),
    [setLocationEditor]
  );

  const columns = useWarehouseLocationsColumns(tagKeys, openLocationEditor);

  const toolbarElement = useMemo(
    () => (
      <GridToolbarV2
        apiRef={apiRef}
        enhancedGridState={enhancedGridState}
        toolbarActionsRight={
          <AgButton variant="primary" onPress={() => setLocationEditor({ location: null })}>
            <RiAddLine size={20} />
            Add Location
          </AgButton>
        }
      />
    ),
    [apiRef, enhancedGridState, setLocationEditor]
  );

  // `slots` takes a constructor, not an element. Returning the SAME memoized element keeps
  // React's identity bail-out, so the toolbar re-renders exactly as often as it did before.
  const toolbar = useCallback(() => toolbarElement, [toolbarElement]);

  return (
    <>
      <PageBody>
        <DataGrid<WarehouseLocation>
          mode="local"
          rows={locations}
          gridKey="settings.inventory.warehouse-locations"
          storageKey={STORAGE_KEY}
          columns={columns}
          apiRef={apiRef}
          slots={{ toolbar }}
          pinnedColumns={{ right: ['actions'] }}
          enhancedGridState={enhancedGridState}
          variant="list"
        />
      </PageBody>

      {editModalOpen && (
        <WarehouseModal open={editModalOpen} warehouse={warehouse} onClose={() => setEditModalOpen(false)} />
      )}

      {groupingsModalOpen && (
        <LocationGroupingsModal
          open={groupingsModalOpen}
          tagKeys={tagKeys}
          onClose={() => {
            setGroupingsModalOpen(false);
            refetchWarehouse();
          }}
          warehouseId={warehouse.id}
        />
      )}

      {locationEditor && (
        <EditLocationModal
          location={locationEditor.location}
          warehouseId={warehouse.id}
          tagKeys={tagKeys}
          onClose={() => setLocationEditor(null)}
        />
      )}
    </>
  );
}
